import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useClip } from "../../store/clip/hooks";
import { useModals } from "../../store/dialogs/hooks";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  // FormControl,
  // FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  inputBaseClasses,
  TextField,
  // useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { ChevronRight, Clear } from "@mui/icons-material";
import { Transition } from "../../pages/app/utils/animation";
// import { CustomSwitch, OutlinedDiv } from "../CustomSwitch";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const parseRecTime = (recTime) => {
  if (recTime === null || recTime === undefined) return null;
  return recTime.endsWith("Z") ? recTime : `${recTime}Z`;
};

const convertToDayJs = (recTime) => {
  if (recTime === null || recTime === undefined) return null;
  return dayjs(recTime);
};

const EditClipSchema = z.object({
  description: z
    .string()
    .trim()
    .nonempty({ message: "A clip name is required." }),
  location: z.string().trim().optional(),
  // sync: z.boolean().optional(),
  recordingTime: z.coerce.date().nullable().optional(),
});

export const EditClipMetaDataDialog = ({ clip, onChange, readOnly }) => {
  // const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditor = location.pathname.includes("/editor");
  const { updateClipMetadata } = useClip();
  const { clipDetailsClipId, showClipDetailsDialog, closeClipDetailsDialog } =
    useModals();
  const recTime = parseRecTime(clip?.recordingTime);
  const initialState = useMemo(() => {
    return {
      caseId: clip?.caseId,
      id: clip?.id,
      description: clip?.description,
      location: clip?.location,
      recordingTime: convertToDayJs(recTime), // set that it is in UTC on the way in
      // sync: clip?.sync,
    };
  }, [clip, recTime]);

  // const canUpdateSync =
  //   clip?.numberOfChannels > 1 && clip?.recordingDeviceId === 1;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { dirtyFields, errors, isValid },
  } = useForm({
    resolver: zodResolver(EditClipSchema),
    values: { ...initialState } || {},
    mode: "all",
  });

  // NOTE: recordingTime is a special case due to how dates are handled in the system, making this a bit more complex than a typical dirty check
  const formIsDirty =
    (initialState.recordingTime?.toISOString() || null) !==
    (watch("recordingTime")?.toISOString() || null)
      ? true
      : Object.keys(dirtyFields).filter((key) => key !== "recordingTime")
          .length > 0;

  const disableSave = !isValid || !formIsDirty || readOnly;

  const handleClose = () => {
    reset();
    closeClipDetailsDialog();
  };

  const handleNameClear = () => {
    setValue("description", "", { shouldDirty: true });
    trigger("description");
  };

  const onSubmit = async (data) => {
    await updateClipMetadata({
      caseId: clip?.caseId,
      id: clip?.id,
      ...data,
      recordingTime: convertToDayJs(data.recordingTime),
    });
    onChange && onChange();
    reset();
    // NOTE: forces page reload and will suffice as wavesurfer is hopefully going away
    if (isEditor) {
      navigate(0);
    }
    closeClipDetailsDialog();
  };

  return (
    <Dialog
      aria-hidden={!showClipDetailsDialog}
      open={showClipDetailsDialog && clipDetailsClipId === clip.id}
      closeAfterTransition={false}
      disableRestoreFocus
      onClose={handleClose}
      slots={{
        transition: Transition,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {`${readOnly ? "View" : "Edit"}`} Clip Details
        </DialogTitle>
        <DialogContent dividers>
          <Grid container mt={1} spacing={3}>
            <Grid size={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Clip Name"
                    error={!!errors.description}
                    helperText={
                      errors.description
                        ? errors.description?.message
                        : "Please enter a name for the clip."
                    }
                    required
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{
                              [`[data-shrink=false] ~ .${inputBaseClasses.root} > &`]:
                                {
                                  opacity: 0,
                                  pointerEvents: "none",
                                },
                            }}
                          >
                            <IconButton
                              aria-label="clear input"
                              onClick={handleNameClear}
                              edge="end"
                              size="small"
                            >
                              <Clear />
                            </IconButton>
                          </InputAdornment>
                        ),
                        readOnly: readOnly,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Location"
                    slotProps={{ input: { readOnly: readOnly } }}
                  />
                )}
              />
            </Grid>
            {/* <Grid size={6}>
              <FormControl
                fullWidth
                sx={{
                  " .MuiFormLabel-root": {
                    color: !readOnly
                      ? theme.palette.text.disabled
                      : theme.palette.text.secondary,
                  },
                  ":hover:not(:focus-within) fieldset.MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: !readOnly
                        ? theme.palette.action.disabled
                        : theme.palette.text.primary,
                    },
                }}
              >
                <OutlinedDiv label="Common Clock *">
                  <Controller
                    name="sync"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <CustomSwitch
                            checked={value}
                            disabled={readOnly || !canUpdateSync}
                            onChange={(e) => onChange(e.target.checked)}
                            slotProps={{
                              input: { "aria-label": "common-clock" },
                            }}
                            sx={{ ml: 1 }}
                          />
                        }
                        label={value ? "On" : "Off"}
                        labelPlacement="start"
                        sx={{ marginLeft: 0 }}
                      />
                    )}
                  />
                </OutlinedDiv>
              </FormControl>
            </Grid> */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="recordingTime"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      label="Date"
                      timezone="UTC"
                      disabled={readOnly}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                      sx={{
                        "& .MuiPickersSectionList-root.MuiPickersInputBase-sectionsContainer":
                          {
                            width: 1,
                          },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="recordingTime"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TimePicker
                      label="Time"
                      timezone="UTC"
                      disabled={readOnly}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                        },
                      }}
                      sx={{
                        "& .MuiPickersSectionList-root.MuiPickersInputBase-sectionsContainer":
                          {
                            width: 1,
                          },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            endIcon={<ChevronRight />}
            disabled={disableSave}
            variant="contained"
            type="submit"
          >
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
